/*
 * The local-mode "backend" — every API surface in src/services/ delegates here
 * when isLocalMode() is true. Reads/writes localStorage for metadata, IDB for
 * blobs, calls the Vite vertexLocalProxy plugin for real generation when an SA
 * is uploaded; falls back to mockGenerator otherwise.
 */

import {
  type Character,
  type Episode,
  type PromptTemplate,
  type Scene,
  type SceneImage,
  type SceneVideo,
  type StylePreset,
  type StyleSettings,
  type TemplateKind,
  SYSTEM_IMAGE_TEMPLATE_ID,
  SYSTEM_VIDEO_TEMPLATE_ID,
} from '@/types';
import { localStore } from './localStore';
import { putBlob, deleteBlob } from './localBlobs';
import { generateMockImage, generateMockVideoFrame } from './mockGenerator';
import { vertexImage, vertexConfigStatus } from './vertexClient';
import {
  CULTURAL_GUIDELINES_TEXT,
  defaultStylePresets,
  defaultSystemTemplates,
} from './defaults';

const APP_SETTING_KEYS = {
  imageModel: 'vertex.model.image',
  videoFastModel: 'vertex.model.video_fast',
  videoFullModel: 'vertex.model.video_full',
} as const;

/**
 * Lazy first-run seed of style presets + system prompt templates so an
 * empty localStorage doesn't make the Style tab look broken. Also kicks off
 * the schema-version migration that drops incompatible v1 blobs.
 *
 * The two system templates are upserted by fixed UUID — same contract as the
 * backend seeder. Re-runs bump the body, never duplicate. Idempotent.
 */
function ensureDefaultsSeeded(): void {
  localStore.ensureSchemaMigrated();

  if (localStore.getStylePresets().length === 0) {
    localStore.putStylePresets(defaultStylePresets());
  }

  const existingGlobals = localStore.getPromptTemplates('__seed-probe__').filter((t) => t.scope === 'global');
  const sysImage = existingGlobals.find((t) => t.id === SYSTEM_IMAGE_TEMPLATE_ID);
  const sysVideo = existingGlobals.find((t) => t.id === SYSTEM_VIDEO_TEMPLATE_ID);
  if (!sysImage || !sysVideo) {
    const otherGlobals = existingGlobals.filter(
      (t) => t.id !== SYSTEM_IMAGE_TEMPLATE_ID && t.id !== SYSTEM_VIDEO_TEMPLATE_ID,
    );
    localStore.putGlobalPromptTemplates([...defaultSystemTemplates(), ...otherGlobals]);
  }
}

function now(): number {
  return Date.now();
}

function uuid(): string {
  return crypto.randomUUID();
}

function ensureEpisode(uuidv: string): Episode {
  const existing = localStore.getEpisode(uuidv);
  if (existing) return existing;
  const seeded: Episode = {
    uuid: uuidv,
    title: `Local Episode ${uuidv.slice(0, 8)}`,
    script: '',
    styleSettings: defaultStyleSettings(),
  };
  localStore.putEpisode(seeded);
  return seeded;
}

function defaultStyleSettings(): StyleSettings {
  // New episodes default to whichever preset is marked isDefault (Cinematic
  // 3D after seeding). active_*_template_id stays null so the system
  // scaffolds apply automatically until the user clones-and-edits.
  ensureDefaultsSeeded();
  const defaultPreset = localStore.getStylePresets().find((p) => p.isDefault);
  return {
    presetId: defaultPreset?.id ?? null,
    aspectRatio: '16:9',
    imageQuality: '2K',
    culturalGuidelines: true,
    activeImageTemplateId: null,
    activeVideoTemplateId: null,
  };
}

function nextOrder(scenes: Scene[]): number {
  return scenes.length > 0 ? Math.max(...scenes.map((s) => s.order)) + 1 : 1;
}

// ---------- Episode bundle ----------

export async function getLocalEpisodeBundle(uuidv: string) {
  ensureDefaultsSeeded();
  const episode = ensureEpisode(uuidv);
  return {
    episode,
    characters: localStore.getCharacters(uuidv),
    scenes: localStore.getScenes(uuidv).filter((s) => !s.deletedAt),
    images: localStore.getImages(uuidv).filter((i) => !i.deletedAt),
    videos: localStore.getVideos(uuidv).filter((v) => !v.deletedAt),
  };
}

// ---------- Scenes ----------

export async function localListScenes(uuidv: string): Promise<Scene[]> {
  return localStore.getScenes(uuidv).filter((s) => !s.deletedAt);
}

export async function localCreateScene(
  uuidv: string,
  fields: Pick<Scene, 'prompt' | 'order' | 'references'>,
): Promise<Scene> {
  const scenes = localStore.getScenes(uuidv);
  const scene: Scene = {
    id: uuid(),
    episodeUuid: uuidv,
    order: fields.order || nextOrder(scenes),
    prompt: fields.prompt,
    references: fields.references,
    imageIds: [],
    videoIds: [],
    heroImageId: null,
    status: 'empty',
    version: 1,
    createdAt: now(),
    updatedAt: now(),
    deletedAt: null,
  };
  localStore.putScenes(uuidv, [...scenes, scene]);
  return scene;
}

export async function localPatchScene(
  uuidv: string,
  id: string,
  fields: Partial<Scene>,
  expectedVersion: number,
): Promise<Scene> {
  const scenes = localStore.getScenes(uuidv);
  const idx = scenes.findIndex((s) => s.id === id);
  if (idx === -1) throw new Error('Scene not found');
  const current = scenes[idx];
  if (current.version !== expectedVersion) {
    const err = new Error('Stale version') as Error & { status: number };
    err.status = 409;
    throw err;
  }
  const updated: Scene = {
    ...current,
    ...fields,
    id: current.id,
    version: current.version + 1,
    updatedAt: now(),
  };
  scenes[idx] = updated;
  localStore.putScenes(uuidv, scenes);
  return updated;
}

export async function localDeleteScene(
  uuidv: string,
  id: string,
  expectedVersion: number,
): Promise<void> {
  await localPatchScene(uuidv, id, { deletedAt: now() }, expectedVersion);
}

// ---------- Images ----------

export async function localGenerateImage(
  uuidv: string,
  sceneId: string,
  body: {
    prompt: string;
    references: { sceneIds: string[]; castIds: string[] };
    fresh: boolean;
  },
): Promise<SceneImage> {
  const scenes = localStore.getScenes(uuidv);
  const scene = scenes.find((s) => s.id === sceneId);
  if (!scene) throw new Error('Scene not found');
  const episode = localStore.getEpisode(uuidv) ?? ensureEpisode(uuidv);

  const blob = await tryGenerateImageBlob(
    body.prompt,
    scene.order,
    episode,
    body.references,
    body.fresh,
  );
  const id = uuid();
  const path = await putBlob(`img-${id}`, blob);

  const image: SceneImage = {
    id,
    sceneId,
    path,
    prompt: body.prompt,
    references: body.references,
    fresh: body.fresh,
    style: {
      presetId: episode.styleSettings.presetId,
      customStyle: episode.styleSettings.customStyle,
      aspectRatio: episode.styleSettings.aspectRatio,
      imageQuality: episode.styleSettings.imageQuality,
      culturalGuidelines: episode.styleSettings.culturalGuidelines,
    },
    version: 1,
    createdAt: now(),
    deletedAt: null,
  };

  const allImages = [...localStore.getImages(uuidv), image];
  localStore.putImages(uuidv, allImages);

  // Update the scene's imageIds + status
  const idx = scenes.findIndex((s) => s.id === sceneId);
  scenes[idx] = {
    ...scenes[idx],
    imageIds: [...scenes[idx].imageIds, image.id],
    status: 'ready',
    version: scenes[idx].version + 1,
    updatedAt: now(),
  };
  localStore.putScenes(uuidv, scenes);

  return image;
}

async function tryGenerateImageBlob(
  userPrompt: string,
  sceneNumber: number,
  episode: Episode,
  references: { sceneIds: string[]; castIds: string[] },
  fresh: boolean,
): Promise<Blob> {
  const status = await vertexConfigStatus();
  if (status.ready) {
    try {
      return await vertexImage({
        prompt: composeImagePromptLocal(userPrompt, episode, references, fresh),
        aspectRatio: episode.styleSettings.aspectRatio,
      });
    } catch (err) {
      // Fallback to mock so the user still sees something. Real
      // error is surfaced by the caller's toast pipeline.
      console.warn('[local] Vertex call failed, falling back to mock:', err);
    }
  }
  return generateMockImage(userPrompt, sceneNumber);
}

/**
 * Local-mode mirror of backend PromptComposer::composeImage. Routes the
 * episode's active image template (or the system default) through the same
 * placeholder-substitution rules so generation against the local Vite
 * proxy produces the same final prompt as a prod call.
 */
function composeImagePromptLocal(
  userPrompt: string,
  episode: Episode,
  references: { sceneIds: string[]; castIds: string[] },
  fresh: boolean,
): string {
  if (fresh) return userPrompt;
  const style = episode.styleSettings;
  const templateId = style.activeImageTemplateId ?? SYSTEM_IMAGE_TEMPLATE_ID;
  const template = localStore
    .getPromptTemplates(episode.uuid)
    .find((t) => t.id === templateId);
  if (!template) return userPrompt;

  const ctx: Record<string, string> = {
    prompt: userPrompt,
    style: resolveStyleDescription(style),
    cultural_guidelines: style.culturalGuidelines ? CULTURAL_GUIDELINES_TEXT : '',
    aspect_ratio: style.aspectRatio,
    cast: resolveCastDescriptions(references.castIds, episode.uuid),
    previous_scenes: resolvePreviousScenes(references.sceneIds, episode.uuid),
  };

  return renderTemplate(template.body, ctx, userPrompt);
}

function resolveStyleDescription(style: StyleSettings): string {
  if (style.presetId) {
    const preset = localStore.getStylePresets().find((p) => p.id === style.presetId);
    const desc = preset?.styleReference?.description;
    if (desc) return desc;
  }
  return style.customStyle?.description ?? '';
}

function resolveCastDescriptions(castIds: string[], uuidv: string): string {
  if (castIds.length === 0) return '';
  const all = localStore.getCharacters(uuidv);
  return all
    .filter((c) => castIds.includes(c.id))
    .map((c) => `- ${c.name}: ${c.description || c.type || 'character'}`)
    .join('\n');
}

function resolvePreviousScenes(sceneIds: string[], uuidv: string): string {
  if (sceneIds.length === 0) return '';
  const all = localStore.getScenes(uuidv);
  return all
    .filter((s) => sceneIds.includes(s.id))
    .sort((a, b) => a.order - b.order)
    .map((s) => `- Scene ${String(s.order).padStart(2, '0')}: ${s.prompt}`)
    .join('\n');
}

/**
 * Substitute {placeholder} tokens. Unknown tokens pass through unchanged.
 * If the body has no {prompt} token at all, append the user prompt.
 */
function renderTemplate(body: string, ctx: Record<string, string>, userPrompt: string): string {
  const hasPromptToken = body.includes('{prompt}');
  const out = body.replace(/\{([a-z_]+)\}/g, (whole, key: string) =>
    Object.prototype.hasOwnProperty.call(ctx, key) ? ctx[key] : whole,
  );
  return hasPromptToken ? out : `${out.replace(/\s+$/, '')}\n\n${userPrompt}`;
}

export async function localDeleteImage(
  uuidv: string,
  _sceneId: string,
  imageId: string,
  expectedVersion: number,
): Promise<void> {
  const images = localStore.getImages(uuidv);
  const idx = images.findIndex((i) => i.id === imageId);
  if (idx === -1) throw new Error('Image not found');
  if (images[idx].version !== expectedVersion) {
    const err = new Error('Stale version') as Error & { status: number };
    err.status = 409;
    throw err;
  }
  images[idx] = { ...images[idx], deletedAt: now(), version: images[idx].version + 1 };
  localStore.putImages(uuidv, images);
  await deleteBlob(`img-${imageId}`);
}

// ---------- Videos ----------

export async function localGenerateVideo(
  uuidv: string,
  sceneId: string,
  body: {
    sourceImageId: string;
    prompt: string;
    duration: 4 | 6 | 8;
    model: 'veo-fast' | 'veo-full';
    extendsVideoId?: string;
  },
): Promise<SceneVideo> {
  const scenes = localStore.getScenes(uuidv);
  const scene = scenes.find((s) => s.id === sceneId);
  if (!scene) throw new Error('Scene not found');

  // Mock video — render a static frame as PNG, save with .mp4-ish placeholder URL
  const blob = await generateMockVideoFrame(body.prompt, scene.order);
  const id = uuid();
  const path = await putBlob(`vid-${id}`, blob);

  const video: SceneVideo = {
    id,
    sceneId,
    sourceImageId: body.sourceImageId,
    extendsVideoId: body.extendsVideoId ?? null,
    path,
    prompt: body.prompt,
    references: { sceneIds: [], castIds: [] },
    fresh: false,
    duration: body.duration,
    model: body.model,
    status: 'ready',
    veoVideoObject: null,
    version: 1,
    createdAt: now(),
    deletedAt: null,
  };

  localStore.putVideos(uuidv, [...localStore.getVideos(uuidv), video]);

  const idx = scenes.findIndex((s) => s.id === sceneId);
  scenes[idx] = {
    ...scenes[idx],
    videoIds: [...scenes[idx].videoIds, video.id],
    version: scenes[idx].version + 1,
    updatedAt: now(),
  };
  localStore.putScenes(uuidv, scenes);

  return video;
}

export async function localDeleteVideo(
  uuidv: string,
  _sceneId: string,
  videoId: string,
  expectedVersion: number,
): Promise<void> {
  const videos = localStore.getVideos(uuidv);
  const idx = videos.findIndex((v) => v.id === videoId);
  if (idx === -1) throw new Error('Video not found');
  if (videos[idx].version !== expectedVersion) {
    const err = new Error('Stale version') as Error & { status: number };
    err.status = 409;
    throw err;
  }
  videos[idx] = { ...videos[idx], deletedAt: now(), version: videos[idx].version + 1 };
  localStore.putVideos(uuidv, videos);
  await deleteBlob(`vid-${videoId}`);
}

// ---------- Style settings ----------

export async function localGetStyleSettings(uuidv: string): Promise<StyleSettings> {
  ensureDefaultsSeeded();
  const stored = localStore.getStyleSettings(uuidv);
  if (!stored) return defaultStyleSettings();
  // Backfill the new fields for any episode whose blob predates the schema-2
  // migration. The migration removes `defaultPromptTemplateId` but a partial
  // run might leave the new fields undefined.
  return {
    ...stored,
    activeImageTemplateId: stored.activeImageTemplateId ?? null,
    activeVideoTemplateId: stored.activeVideoTemplateId ?? null,
  };
}

export async function localPutStyleSettings(
  uuidv: string,
  settings: StyleSettings,
): Promise<StyleSettings> {
  localStore.putStyleSettings(uuidv, settings);
  const ep = localStore.getEpisode(uuidv);
  if (ep) localStore.putEpisode({ ...ep, styleSettings: settings });
  return settings;
}

// ---------- Demo seed ----------

export async function seedDemoEpisode(): Promise<string> {
  // Wipe existing local data for a clean demo state
  localStore.clearAll();
  const uuidv = 'demo';
  const episode: Episode = {
    uuid: uuidv,
    title: 'Demo — Aida and the Honey Cat',
    script:
      'Aida wakes up in the garden. Honey Cat appears and leads her to the bee tree. Together they discover the bees, the queen, and the secret of the honeycomb.',
    styleSettings: defaultStyleSettings(),
  };
  localStore.putEpisode(episode);

  const characters: Character[] = [
    { id: 'c-1', name: 'Aida', description: 'A bright-eyed seven-year-old.', type: 'character', imagePath: '' },
    { id: 'c-2', name: 'Honey Cat', description: 'Ginger cat companion.', type: 'character', imagePath: '' },
    { id: 'c-3', name: 'Garden', description: 'Summer flower garden.', type: 'location', imagePath: '' },
  ];
  localStore.putCharacters(uuidv, characters);

  const prompts = [
    'Aida steps into the sunlit garden, a tiny ginger cat watching from the bench.',
    'Honey Cat sniffs a bright sunflower; Aida laughs.',
    'They follow a trail of bees toward an old oak tree.',
    'A door at the base of the tree creaks open.',
    'Inside the hive, the queen bee greets them.',
    'Aida is offered a single drop of golden honey.',
    'Aida and Honey Cat thank the bees.',
    'Back in the garden, the sun sets warm and slow.',
  ];

  const scenes: Scene[] = prompts.map((prompt, i) => ({
    id: `s-${i + 1}`,
    episodeUuid: uuidv,
    order: i + 1,
    prompt,
    references: {
      sceneIds: i === 0 ? [] : [`s-${i}`],
      castIds: ['c-1', 'c-2'],
    },
    imageIds: [],
    videoIds: [],
    heroImageId: null,
    status: 'empty',
    version: 1,
    createdAt: now() - (prompts.length - i) * 60_000,
    updatedAt: now() - (prompts.length - i) * 60_000,
    deletedAt: null,
  }));
  localStore.putScenes(uuidv, scenes);

  ensureDefaultsSeeded();

  return uuidv;
}

// ---------- Style presets (admin) ----------

export async function localListStylePresets(): Promise<StylePreset[]> {
  ensureDefaultsSeeded();
  return localStore.getStylePresets();
}

export async function localCreateStylePreset(
  input: Omit<StylePreset, 'id' | 'isDefault'> & { isDefault?: boolean },
): Promise<StylePreset> {
  const presets = localStore.getStylePresets();
  const isDefault = !!input.isDefault;
  // Single-default invariant: demote any existing default if this one claims it.
  const others = isDefault ? presets.map((p) => ({ ...p, isDefault: false })) : presets;
  const preset: StylePreset = { id: uuid(), isDefault, ...input };
  localStore.putStylePresets([...others, preset]);
  return preset;
}

export async function localDeleteStylePreset(id: string): Promise<void> {
  localStore.putStylePresets(localStore.getStylePresets().filter((p) => p.id !== id));
}

// ---------- Prompt templates ----------

export async function localListPromptTemplates(uuidv: string): Promise<PromptTemplate[]> {
  ensureDefaultsSeeded();
  // Match backend behavior: system + this episode's customizations. We never
  // expose other episodes' templates here.
  const all = localStore.getPromptTemplates(uuidv);
  return all.filter(
    (t) => t.isSystem || (t.scope === 'episode' && t.episodeUuid === uuidv),
  );
}

export async function localCreatePromptTemplate(
  uuidv: string,
  input: { kind: TemplateKind; name: string; body: string },
): Promise<PromptTemplate> {
  const own = localStore.getPromptTemplates(uuidv).filter(
    (t) => t.scope === 'episode' && t.episodeUuid === uuidv,
  );
  const tpl: PromptTemplate = {
    id: uuid(),
    scope: 'episode',
    episodeUuid: uuidv,
    kind: input.kind,
    isSystem: false,
    clonedFromId: null,
    name: input.name,
    body: input.body,
  };
  localStore.putEpisodePromptTemplates(uuidv, [...own, tpl]);
  return tpl;
}

export async function localUpdatePromptTemplate(
  uuidv: string,
  id: string,
  patch: { name?: string; body?: string },
): Promise<PromptTemplate> {
  const own = localStore.getPromptTemplates(uuidv).filter(
    (t) => t.scope === 'episode' && t.episodeUuid === uuidv,
  );
  const idx = own.findIndex((t) => t.id === id);
  if (idx === -1) throw new Error('Template not found');
  if (own[idx].isSystem) throw new Error('System templates are read-only.');
  const updated: PromptTemplate = { ...own[idx], ...patch };
  own[idx] = updated;
  localStore.putEpisodePromptTemplates(uuidv, own);
  return updated;
}

/**
 * Atomic clone-and-activate: copies the source template into a new episode-
 * scoped row and flips the appropriate active_*_template_id slot on the
 * episode's style settings. Mirrors the backend clone() endpoint.
 */
export async function localClonePromptTemplate(
  uuidv: string,
  sourceId: string,
  name?: string,
): Promise<PromptTemplate> {
  // Source can be a system template (most common) or an existing episode row.
  const all = localStore.getPromptTemplates(uuidv);
  const source = all.find((t) => t.id === sourceId);
  if (!source) throw new Error('Source template not found');

  const cloned: PromptTemplate = {
    id: uuid(),
    scope: 'episode',
    episodeUuid: uuidv,
    kind: source.kind,
    isSystem: false,
    clonedFromId: source.id,
    name: name ?? `Custom ${source.kind} template`,
    body: source.body,
  };

  const own = all.filter((t) => t.scope === 'episode' && t.episodeUuid === uuidv);
  localStore.putEpisodePromptTemplates(uuidv, [...own, cloned]);

  // Activate the clone for the matching slot.
  const style = (await localGetStyleSettings(uuidv));
  const next: StyleSettings =
    source.kind === 'image'
      ? { ...style, activeImageTemplateId: cloned.id }
      : { ...style, activeVideoTemplateId: cloned.id };
  await localPutStyleSettings(uuidv, next);

  return cloned;
}

export async function localListGlobalPromptTemplates(): Promise<PromptTemplate[]> {
  ensureDefaultsSeeded();
  return localStore.getPromptTemplates('__none__').filter((t) => t.scope === 'global');
}

export async function localCreateGlobalPromptTemplate(
  input: { kind: TemplateKind; name: string; body: string },
): Promise<PromptTemplate> {
  const all = (await localListGlobalPromptTemplates());
  const tpl: PromptTemplate = {
    id: uuid(),
    scope: 'global',
    episodeUuid: null,
    kind: input.kind,
    isSystem: false,
    clonedFromId: null,
    name: input.name,
    body: input.body,
  };
  localStore.putGlobalPromptTemplates([...all, tpl]);
  return tpl;
}

/**
 * Delete a template. The promptTemplatesApi calls this with the literal
 * '__GLOBAL__' uuid placeholder when the row is global; otherwise it's an
 * episode uuid. System rows are rejected either way.
 */
export async function localDeletePromptTemplate(uuidv: string, id: string): Promise<void> {
  if (uuidv === '__GLOBAL__') {
    const globals = await localListGlobalPromptTemplates();
    const target = globals.find((t) => t.id === id);
    if (target?.isSystem) throw new Error('System templates cannot be deleted.');
    localStore.putGlobalPromptTemplates(globals.filter((t) => t.id !== id));
    return;
  }

  const own = localStore.getPromptTemplates(uuidv).filter(
    (t) => t.scope === 'episode' && t.episodeUuid === uuidv,
  );
  const target = own.find((t) => t.id === id);
  if (!target) throw new Error('Template not found');
  if (target.isSystem) throw new Error('System templates cannot be deleted.');

  // If this template was active on the episode, clear the slot so the user
  // falls back to the system default.
  const style = await localGetStyleSettings(uuidv);
  const cleared: StyleSettings = {
    ...style,
    activeImageTemplateId: style.activeImageTemplateId === id ? null : style.activeImageTemplateId,
    activeVideoTemplateId: style.activeVideoTemplateId === id ? null : style.activeVideoTemplateId,
  };
  if (cleared.activeImageTemplateId !== style.activeImageTemplateId
      || cleared.activeVideoTemplateId !== style.activeVideoTemplateId) {
    await localPutStyleSettings(uuidv, cleared);
  }

  localStore.putEpisodePromptTemplates(uuidv, own.filter((t) => t.id !== id));
}

export function reseedLocalDefaults(): void {
  localStore.putStylePresets(defaultStylePresets());
  // Replace globals entirely — defaultSystemTemplates returns the canonical
  // two system rows. Any user-created globals are nuked, which matches the
  // "Reseed defaults" button's intent.
  localStore.putGlobalPromptTemplates(defaultSystemTemplates());
}

// ---------- Model settings (admin Models page mirror) ----------

export async function localGetModelSettings(): Promise<{
  imageModel: string;
  videoFastModel: string;
  videoFullModel: string;
}> {
  return {
    imageModel: localStore.getAppSetting(APP_SETTING_KEYS.imageModel)
      ?? 'gemini-3-pro-image-preview',
    videoFastModel: localStore.getAppSetting(APP_SETTING_KEYS.videoFastModel)
      ?? 'veo-3.1-fast-generate-preview',
    videoFullModel: localStore.getAppSetting(APP_SETTING_KEYS.videoFullModel)
      ?? 'veo-3.1-generate-preview',
  };
}

export async function localPutModelSettings(input: {
  imageModel: string;
  videoFastModel: string;
  videoFullModel: string;
}): Promise<{ imageModel: string; videoFastModel: string; videoFullModel: string }> {
  localStore.putAppSetting(APP_SETTING_KEYS.imageModel, input.imageModel);
  localStore.putAppSetting(APP_SETTING_KEYS.videoFastModel, input.videoFastModel);
  localStore.putAppSetting(APP_SETTING_KEYS.videoFullModel, input.videoFullModel);
  return localGetModelSettings();
}

// ---------- Cast (characters) ----------

export async function localCreateCharacter(
  uuidv: string,
  body: { name: string; type: Character['type']; description?: string; imageDataUrl?: string },
): Promise<Character> {
  let imagePath = '';
  if (body.imageDataUrl) {
    const blob = await dataUrlToBlob(body.imageDataUrl);
    imagePath = await putBlob(uuid(), blob);
  }
  const character: Character = {
    id: uuid(),
    name: body.name,
    description: body.description ?? '',
    type: body.type,
    imagePath,
  };
  const list = localStore.getCharacters(uuidv);
  localStore.putCharacters(uuidv, [...list, character]);
  return character;
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl);
  return res.blob();
}
